import type { Message } from '../../../contracts/harness.types';
import { artifactKey, turnMessageKey } from './keys';
import { persistedUserCount, reconcileAcceptedOptimistic } from './reconcile';
import {
  appendPositional,
  beginDraft,
  bindKeys,
  commitDraft,
  mintPositionalId,
  resolveEntryId,
  withdrawEntry,
  type TranscriptDraft,
} from './store';
import type { TranscriptStore, UserTranscriptEntry } from './types';

export interface OptimisticTurnInput {
  /** Client-minted at ENQUEUE, so a queued message carries identity before dispatch. */
  turnId: string;
  messages: Message[];
}

/**
 * INGESTOR 3 — the local one. The bubble on screen is the same ENTRY the backend
 * copy later upgrades, not a lookalike something has to reconcile.
 */
export const ingestOptimisticTurn = (
  store: TranscriptStore,
  { turnId, messages }: OptimisticTurnInput
): TranscriptStore => {
  const draft = beginDraft(store);
  // Counted off the store, not `run.messages`: `reconcile` reads the same ledger,
  // so the two cannot drift on a row only one of them classifies.
  const baselineUserCount = persistedUserCount(store);

  messages.forEach((message, position) => {
    const key = turnMessageKey(turnId, position);
    if (draft.keys[key]) return;
    const entry: UserTranscriptEntry = {
      id: mintPositionalId(draft),
      kind: 'user',
      source: 'optimistic',
      message,
      turnId,
      position,
      baselineUserCount,
      accepted: false,
    };
    appendPositional(draft, entry);
    draft.keys[key] = entry.id;
  });

  return commitDraft(store, draft);
};

export const acceptOptimisticTurn = (
  store: TranscriptStore,
  turnId: string
): TranscriptStore => {
  const draft = beginDraft(store);
  forEachTurnEntry(draft, turnId, (entry) => {
    if (entry.accepted) return;
    draft.byId[entry.id] = { ...entry, accepted: true };
    draft.changed = true;
  });
  reconcileAcceptedOptimistic(draft);
  return commitDraft(store, draft);
};

/** The POST echoed artifact ids, so each entry gains its backend key and the next
 *  fetch lands on it by id — no signature guessing, no positional arithmetic. */
export const aliasOptimisticTurn = (
  store: TranscriptStore,
  {
    turnId,
    backendTurnId,
    userMessageIds,
  }: { turnId: string; backendTurnId?: string; userMessageIds: readonly string[] }
): TranscriptStore => {
  const draft = beginDraft(store);
  forEachTurnEntry(draft, turnId, (entry) => {
    const artifactId = userMessageIds[entry.position];
    const nextTurnId = backendTurnId ?? entry.turnId;
    if (artifactId) {
      const keys = [artifactKey(artifactId)];
      if (backendTurnId) keys.push(turnMessageKey(backendTurnId, entry.position));
      bindKeys(draft, entry.id, keys);
    }
    if (entry.accepted && entry.turnId === nextTurnId) return;
    draft.byId[entry.id] = { ...entry, accepted: true, turnId: nextTurnId };
    draft.changed = true;
  });
  reconcileAcceptedOptimistic(draft);
  return commitDraft(store, draft);
};

export const withdrawOptimisticTurn = (
  store: TranscriptStore,
  turnId: string,
  messageCount: number
): TranscriptStore => {
  let next = store;
  for (let position = 0; position < messageCount; position += 1) {
    next = withdrawEntry(next, turnMessageKey(turnId, position));
  }
  return next;
};

const forEachTurnEntry = (
  draft: TranscriptDraft,
  turnId: string,
  visit: (entry: UserTranscriptEntry) => void
): void => {
  for (let position = 0; ; position += 1) {
    const entryId = resolveEntryId(draft, [turnMessageKey(turnId, position)]);
    if (!entryId) break;
    const entry = draft.byId[entryId];
    if (entry?.kind !== 'user') break;
    visit(entry);
  }
};
